import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { uploadAndParse } from "@/lib/api";
import { getSession } from "@/lib/authApi";
import { APP_VERSION } from "@/lib/version";
import ManageBilling from "@/components/auth/ManageBilling";

// Matches the approved mockup #3 — cream ground, "Pharmacy Analyzer" wordmark
// top-left, v1.0 pill top-right, serif headline, primary upload button,
// progress bar shown mid-parse, "Processed in-memory / Your data is never
// stored on our servers" footer, plus a "signed in as <pharmacy>" chip.
export default function UploadView({ onAnalysisReady }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [billingOpen, setBillingOpen] = useState(false);

  const session = getSession();

  // Backgrounds and scroll-lock are handled globally in index.html (inline)
  // and index.css, so no per-mount body-style overrides here — the previous
  // approach caused a one-frame repaint that showed as a visible "flash"
  // during PWA launch. Only scroll-reset remains, which is needed to clear
  // any stale scroll offset carried over from the (position:fixed) Login
  // screen when we transition into UploadView.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setLoading(true);
    setUploadPct(0);
    setPhase("uploading");
    const MIN_UPLOAD_MS = 600;
    const uploadStartTs = performance.now();
    let pendingFullProgress = false;
    try {
      const data = await uploadAndParse(file, (pct) => {
        if (pct === null) {
          pendingFullProgress = false;
          setPhase("parsing");
          setUploadPct(100);
          return;
        }
        setUploadPct(pct);
        if (pct >= 100) {
          const elapsed = performance.now() - uploadStartTs;
          if (elapsed >= MIN_UPLOAD_MS) {
            setPhase("parsing");
          } else {
            pendingFullProgress = true;
            setTimeout(() => { if (pendingFullProgress) setPhase("parsing"); },
              MIN_UPLOAD_MS - elapsed);
          }
        }
      });
      pendingFullProgress = false;
      setPhase("parsing");
      toast.success("Data uploaded successfully");
      onAnalysisReady(data);
    } catch (err) {
      pendingFullProgress = false;
      const detail = err?.response?.data?.detail || err?.message || "Failed to parse the PDF.";
      toast.error(String(detail));
    } finally {
      setLoading(false);
      setPhase("idle");
      setUploadPct(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!loading) handleFile(e.dataTransfer.files?.[0]);
  };

  const triggerPicker = () => { if (!loading) inputRef.current?.click(); };

  const signOut = () => window.dispatchEvent(new CustomEvent("authSignOut"));

  return (
    <div
      className="upload-shell"
      data-testid="upload-view"
      onDragOver={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <style>{styles}</style>

      <div className="uv-topbar">
        <div className="uv-brand" data-testid="brand-wordmark">
          Pharmacy <em>Analyzer</em>
        </div>
        <div className="uv-topbar-right">
          {session?.pharmacy_name ? (
            <>
              <button
                type="button"
                className="uv-billing-top"
                onClick={() => setBillingOpen(true)}
                data-testid="manage-billing-btn"
                aria-label="Manage billing"
                title="Manage billing"
              >
                Billing
              </button>
              <button
                type="button"
                className="uv-signout-top"
                onClick={signOut}
                data-testid="sign-out-btn"
                aria-label={`Sign out of ${session.pharmacy_name}`}
                title={`Signed in as ${session.pharmacy_name}`}
              >
                Sign out
              </button>
            </>
          ) : null}
          <span className="uv-ver">{APP_VERSION}</span>
        </div>
      </div>

      <ManageBilling open={billingOpen} onClose={() => setBillingOpen(false)} />

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        data-testid="pdf-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className={`uv-body ${dragOver ? "drag-over" : ""}`} data-testid="pdf-dropzone">
        <div className="uv-headline">
          Upload your<br /><em>pharmacy report</em>
        </div>
        <div className="uv-subhead">
          Know exactly how your pharmacy<br />is performing — anytime, at your fingertips.
        </div>

        {loading ? (
          <div className="uv-progress" data-testid="upload-progress" aria-live="polite">
            <div className="uv-progress-label">
              {phase === "uploading" ? `Uploading… ${uploadPct}%` : "Processing data…"}
            </div>
            {phase === "uploading" ? (
              <div className="uv-progress-bar">
                <div className="fill" style={{ width: `${uploadPct}%` }} />
              </div>
            ) : (
              <div className="uv-progress-bar indet">
                <div className="stripe" />
              </div>
            )}
          </div>
        ) : null}

        <button
          type="button"
          className="uv-primary"
          onClick={triggerPicker}
          disabled={loading}
          data-testid="upload-cta"
        >
          <UploadIcon />
          {loading ? "Please wait…" : "Upload PDF report"}
        </button>
        {dragOver ? (
          <div className="uv-drag-hint">Release to upload your PDF</div>
        ) : null}
      </div>

      <div className="uv-foot">
        <strong>Processed in-memory.</strong><br />
        Your data is never stored on our servers.
      </div>
    </div>
  );
}

function UploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

const styles = `
/* Fonts (Fraunces, Geist, Inter, Space Grotesk) are preloaded in index.html
   so they're ready before this component mounts — no @import here to avoid
   a font-swap flash on PWA launch. */

.upload-shell {
  min-height: 100vh;
  min-height: 100dvh;
  background: #F7F3EC;
  color: #1B3B2E;
  font-family: 'Geist', 'Inter', -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  -webkit-font-smoothing: antialiased;
  transition: background 0.15s;
}
.upload-shell.drag-over { background: #F0E9DA; }
.uv-topbar {
  padding-top: max(52px, calc(26px + env(safe-area-inset-top)));
  padding-left: 24px;
  padding-right: 24px;
  display: flex; align-items: center; justify-content: space-between;
}
.uv-brand {
  font-family: 'Fraunces', serif;
  font-size: 17px;
  font-weight: 400;
  letter-spacing: -0.015em;
  color: #1B3B2E;
}
.uv-brand em { font-style: italic; color: #2B5E4A; font-weight: 400; }
.uv-topbar-right {
  display: inline-flex; align-items: center; gap: 8px;
}
.uv-signout-top {
  font-family: 'Geist','Inter';
  font-size: 10px;
  font-weight: 500;
  color: #9B8666;
  background: rgba(93,74,40,0.08);
  border: none;
  padding: 3px 9px;
  border-radius: 100px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.uv-signout-top:hover { background: rgba(93,74,40,0.14); color: #5D4A28; }
.uv-signout-top:active { opacity: 0.7; }
.uv-billing-top {
  font-family: 'Geist','Inter';
  font-size: 10px;
  font-weight: 600;
  color: #FBF6E9;
  background: #1B3B2E;
  border: none;
  padding: 3px 9px;
  border-radius: 100px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.05s;
}
.uv-billing-top:hover { opacity: 0.9; }
.uv-billing-top:active { transform: scale(0.96); }
.uv-ver {
  font-family: 'Geist','Inter';
  font-size: 10px;
  color: #9B8666;
  background: rgba(93,74,40,0.08);
  padding: 3px 9px;
  border-radius: 100px;
  font-weight: 500;
}
.uv-body {
  flex: 1;
  padding: 60px 30px 20px;
  display: flex; flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 460px;
  width: 100%;
  margin: 0 auto;
}
.uv-headline {
  font-family: 'Fraunces', serif;
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #1B3B2E;
  margin-bottom: 16px;
}
.uv-headline em { font-style: italic; color: #2B5E4A; font-weight: 400; }
.uv-subhead {
  font-size: 13px;
  color: #5D4A28;
  line-height: 1.55;
  max-width: 300px;
  margin: 0 auto 30px;
}
.uv-progress {
  width: 100%;
  max-width: 260px;
  margin: 0 auto 16px;
}
.uv-progress-label {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-size: 13px;
  color: #1B3B2E;
  margin-bottom: 10px;
}
.uv-progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(93,74,40,0.15);
  border-radius: 100px;
  overflow: hidden;
  position: relative;
}
.uv-progress-bar .fill {
  height: 100%;
  background: linear-gradient(90deg, #2B5E4A, #1B3B2E);
  border-radius: 100px;
  transition: width 0.2s;
}
.uv-progress-bar.indet .stripe {
  position: absolute; inset-block: 0;
  width: 33%;
  background: linear-gradient(90deg, transparent, #2B5E4A, transparent);
  border-radius: 100px;
  animation: uv-indet 1.4s ease-in-out infinite;
}
@keyframes uv-indet {
  0% { left: -35%; }
  100% { left: 100%; }
}
.uv-primary {
  background: #1B3B2E;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  color: white;
  font-family: 'Geist','Inter';
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 auto 8px;
  cursor: pointer;
  min-width: 240px;
  transition: opacity 0.15s, transform 0.05s;
}
.uv-primary:hover:not(:disabled) { opacity: 0.9; }
.uv-primary:active:not(:disabled) { transform: scale(0.98); }
.uv-primary:disabled { opacity: 0.5; cursor: default; }
.uv-drag-hint {
  font-size: 11.5px;
  color: #7A6947;
  margin-top: 4px;
}
.uv-foot {
  padding: 14px 30px calc(24px + env(safe-area-inset-bottom));
  text-align: center;
  font-size: 10.5px;
  color: #7A6947;
  line-height: 1.5;
}
.uv-foot strong {
  color: #1B3B2E;
  font-weight: 600;
  font-family: 'Fraunces';
  font-style: italic;
  font-size: 11.5px;
}
.hidden { display: none; }
`;
